import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Layers, Info, Building2, Hospital, Home, HeartPulse, ChevronDown, ArrowUpRight } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import Breadcrumb from '../components/Breadcrumb';
import { api } from '../api';

const US_STATES = ['AL','AK','AZ','AR','CA','CO','CT','DE','DC','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY','PR','GU','VI','AS','MP'];

const ENTITY_META = {
  snf:      { label: 'SNFs',          icon: Building2, accent: '#1A2622' },
  hospital: { label: 'Hospitals',     icon: Hospital,  accent: '#34538F' },
  hha:      { label: 'Home Health',   icon: Home,      accent: '#2D5F4F' },
  hospice:  { label: 'Hospice',       icon: HeartPulse, accent: '#A06628' },
};

const CLASS_COLOR = {
  'For-Profit':  '#A06628',
  'Non-Profit':  '#2D5F4F',
  'Government':  '#34538F',
  'Other':       '#9A9077',
  'Unknown':     '#C9C0A8',
};

function fmtInt(n) { if (n == null) return '—'; return Number(n).toLocaleString(); }
function fmtRatio(n) { if (n == null) return '—'; return Number(n).toFixed(2); }

function OwnershipBar({ rows }) {
  if (!rows || !rows.length) return <div className="h-3 rounded bg-[#F0E7D2]" />;
  return (
    <div className="flex w-full h-3 rounded overflow-hidden border border-[#F0E7D2]">
      {rows.map((r) => (
        <div key={r.class}
             title={`${r.class}: ${r.count} (${r.pct}%)`}
             style={{ width: `${r.pct}%`, background: CLASS_COLOR[r.class] || '#9A9077' }} />
      ))}
    </div>
  );
}

function OwnershipLegend({ rows }) {
  return (
    <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[10.5px]">
      {(rows || []).map((r) => (
        <span key={r.class} className="inline-flex items-center gap-1.5 text-[#3A4A44]">
          <span className="w-2.5 h-2.5 rounded-sm" style={{ background: CLASS_COLOR[r.class] || '#9A9077' }}></span>
          <span className="font-semibold">{r.class}</span>
          <span className="text-[#7A857F] tabular-nums">{r.pct}%</span>
        </span>
      ))}
    </div>
  );
}

function EntityCard({ kind, count, ownership }) {
  const meta = ENTITY_META[kind];
  const Icon = meta.icon;
  return (
    <div className="p-4 rounded-xl bg-white border border-[#E5DCC4]" data-testid={`continuum-entity-${kind}`}>
      <div className="flex items-center gap-2">
        <Icon size={14} style={{ color: meta.accent }} />
        <span className="text-[10.5px] uppercase tracking-wider text-[#7A857F]">{meta.label}</span>
        <span className="ml-auto font-display text-2xl tabular-nums" style={{ color: meta.accent }}>{fmtInt(count)}</span>
      </div>
      <div className="mt-3"><OwnershipBar rows={ownership} /></div>
      <OwnershipLegend rows={ownership} />
    </div>
  );
}

function Ratio({ label, value, sub, accent }) {
  return (
    <div className="p-4 rounded-xl bg-white border border-[#E5DCC4]">
      <div className={`font-display text-3xl tabular-nums ${accent || 'text-[#1A2622]'}`}>{fmtRatio(value)}</div>
      <div className="mt-1 text-[10.5px] uppercase tracking-wider text-[#7A857F]">{label}</div>
      {sub != null && <div className="text-[11px] text-[#5A6660] mt-1">National avg: <span className="tabular-nums">{fmtRatio(sub)}</span></div>}
    </div>
  );
}

export default function ContinuumPage() {
  const [state, setState] = useState('');           // '' = national
  const [data, setData] = useState(null);
  const [national, setNational] = useState(null);   // always loaded once for comparison
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const fetcher = state
      ? api.continuumState(state)
      : api.continuumNational(10);
    fetcher.then((d) => { setData(d); setLoading(false); }).catch(() => setLoading(false));
  }, [state]);

  useEffect(() => {
    api.continuumNational(10).then(setNational).catch(() => {});
  }, []);

  const c = data?.counts || {};
  const r = data?.ratios || {};
  const own = data?.ownership_split || {};
  const ops = data?.multi_entity_operators || [];

  return (
    <div className="min-h-screen">
      <Navbar />
      <main className="max-w-[1280px] mx-auto px-6 lg:px-8 pt-6 pb-10">
        <Breadcrumb items={[{ label: 'Home', to: '/' }, { label: 'Intelligence', to: '/managed-care' }, { label: 'Continuum of Care' }]} />

        <header className="mt-2 mb-6 flex items-start justify-between gap-6 flex-wrap">
          <div>
            <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-[10.5px] font-semibold uppercase tracking-wider bg-[#E5F0EA] text-[#2D5F4F]">
              <Layers size={11} /> Cross-entity intelligence
            </div>
            <h1 className="font-display text-4xl sm:text-5xl tracking-tight text-[#1A2622] mt-2">Continuum of Care</h1>
            <p className="mt-2 text-[13px] text-[#5A6660] max-w-[680px]">
              Cross-overlap between SNFs, Hospitals, Home Health, and Hospice — ownership composition,
              continuum-supply density, and multi-entity operators in any state.
            </p>
          </div>
          <div className="relative">
            <select data-testid="continuum-state-picker" value={state} onChange={(e) => setState(e.target.value)} className="appearance-none bg-white border border-[#E5DCC4] rounded-lg h-11 pl-3.5 pr-9 text-[13px] outline-none cursor-pointer focus:border-[#4A7C5E]">
              <option value="">National (all states)</option>
              {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-[#7A857F]" />
          </div>
        </header>

        {loading && <div className="text-sm text-[#7A857F]">Loading continuum intelligence…</div>}

        {!loading && data && (
          <>
            <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="continuum-entity-grid">
              {['snf', 'hospital', 'hha', 'hospice'].map((k) => (
                <EntityCard key={k} kind={k} count={c[k]} ownership={own[k] || []} />
              ))}
            </section>

            <section className="mt-5">
              <div className="flex items-baseline gap-2 mb-2">
                <h2 className="font-display text-base font-semibold text-[#1A2622]">Post-acute supply density</h2>
                <span className="text-[10.5px] uppercase tracking-wider text-[#7A857F]">per SNF — {state || 'national'}</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <Ratio label="Home Health per SNF" value={r.hha_per_snf} accent="text-[#2D5F4F]"
                       sub={state ? national?.ratios?.hha_per_snf : undefined} />
                <Ratio label="Hospice per SNF" value={r.hospice_per_snf} accent="text-[#A06628]"
                       sub={state ? national?.ratios?.hospice_per_snf : undefined} />
                <Ratio label="Hospital per SNF" value={r.hospital_per_snf} accent="text-[#34538F]"
                       sub={state ? national?.ratios?.hospital_per_snf : undefined} />
              </div>
            </section>

            {!state && (data.top_states_by_post_acute_supply || []).length > 0 && (
              <section className="mt-6 rounded-xl bg-white border border-[#E5DCC4] overflow-hidden">
                <div className="px-5 py-3 border-b border-[#F0E7D2] flex items-center gap-2">
                  <h2 className="font-display text-base font-semibold text-[#1A2622]">Top states by post-acute supply</h2>
                  <span className="ml-auto text-[10.5px] uppercase tracking-wider text-[#7A857F]">ranked by HHA-per-SNF</span>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-[12.5px]">
                    <thead className="bg-[#F5EFDD] text-[10.5px] uppercase tracking-wider text-[#5A6660]">
                      <tr>
                        <th className="text-left px-4 py-2.5">State</th>
                        <th className="text-right px-4 py-2.5">SNFs</th>
                        <th className="text-right px-4 py-2.5">Hospitals</th>
                        <th className="text-right px-4 py-2.5">HHAs</th>
                        <th className="text-right px-4 py-2.5">Hospices</th>
                        <th className="text-right px-4 py-2.5">HHA / SNF</th>
                        <th className="text-right px-4 py-2.5">Hospice / SNF</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.top_states_by_post_acute_supply.map((s) => (
                        <tr key={s.state} className="border-t border-[#F0E7D2] hover:bg-[#FBF6EC] cursor-pointer" onClick={() => setState(s.state)} data-testid={`continuum-top-state-${s.state}`}>
                          <td className="px-4 py-2.5 font-semibold text-[#1A2622]">{s.state}</td>
                          <td className="px-4 py-2.5 text-right tabular-nums">{fmtInt(s.snf)}</td>
                          <td className="px-4 py-2.5 text-right tabular-nums">{fmtInt(s.hospital)}</td>
                          <td className="px-4 py-2.5 text-right tabular-nums text-[#2D5F4F]">{fmtInt(s.hha)}</td>
                          <td className="px-4 py-2.5 text-right tabular-nums text-[#A06628]">{fmtInt(s.hospice)}</td>
                          <td className="px-4 py-2.5 text-right tabular-nums font-semibold text-[#2D5F4F]">{fmtRatio(s.hha_per_snf)}</td>
                          <td className="px-4 py-2.5 text-right tabular-nums text-[#A06628]">{fmtRatio(s.hospice_per_snf)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            )}

            <section className="mt-6 rounded-xl bg-white border border-[#E5DCC4] overflow-hidden" data-testid="continuum-operators">
              <div className="px-5 py-3 border-b border-[#F0E7D2] flex items-center gap-2 flex-wrap">
                <h2 className="font-display text-base font-semibold text-[#1A2622]">Multi-entity operators</h2>
                <span className="text-[10.5px] uppercase tracking-wider text-[#7A857F]">brands that appear across ≥2 entity types in {state || 'the U.S.'}</span>
                <span className="ml-auto text-[10.5px] text-[#A8B0AA]">{ops.length} candidates</span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-[12.5px]">
                  <thead className="bg-[#F5EFDD] text-[10.5px] uppercase tracking-wider text-[#5A6660]">
                    <tr>
                      <th className="text-left px-4 py-2.5">Brand</th>
                      <th className="text-right px-4 py-2.5">Types touched</th>
                      <th className="text-right px-4 py-2.5">SNFs</th>
                      <th className="text-right px-4 py-2.5">Hospitals</th>
                      <th className="text-right px-4 py-2.5">HHAs</th>
                      <th className="text-right px-4 py-2.5">Hospices</th>
                      <th className="text-left px-4 py-2.5">Examples</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ops.length === 0 && <tr><td colSpan={7} className="px-4 py-8 text-center text-[#7A857F]">No multi-entity operators detected in this scope.</td></tr>}
                    {ops.map((o) => (
                      <tr key={o.brand} className="border-t border-[#F0E7D2]" data-testid={`continuum-op-${o.brand.replace(/\s+/g,'-')}`}>
                        <td className="px-4 py-2.5 font-semibold text-[#1A2622]">{o.brand}</td>
                        <td className="px-4 py-2.5 text-right">
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10.5px] font-semibold bg-[#E0EAF8] text-[#34538F]">{o.entity_type_count}</span>
                        </td>
                        <td className="px-4 py-2.5 text-right tabular-nums">{o.snf_count || '—'}</td>
                        <td className="px-4 py-2.5 text-right tabular-nums">{o.hospital_count || '—'}</td>
                        <td className="px-4 py-2.5 text-right tabular-nums text-[#2D5F4F]">{o.hha_count || '—'}</td>
                        <td className="px-4 py-2.5 text-right tabular-nums text-[#A06628]">{o.hospice_count || '—'}</td>
                        <td className="px-4 py-2.5 text-[11px] text-[#5A6660]">
                          <div className="truncate max-w-[280px]" title={(o.examples || []).join(' · ')}>{(o.examples || []).slice(0, 2).join(' · ') || '—'}</div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            <div className="mt-5 flex items-start gap-2 text-[11.5px] text-[#A8B0AA] leading-relaxed">
              <Info size={12} className="mt-0.5 shrink-0" />
              <span><strong>Methodology:</strong> {data.methodology} <strong>Source:</strong> {data.source}</span>
            </div>

            <div className="mt-5 flex flex-wrap items-center gap-2 text-[11.5px] text-[#7A857F]">
              <Link to="/home-health" className="inline-flex items-center gap-1 hover:text-[#2D5F4F]">Home Health directory <ArrowUpRight size={11}/></Link>
              <span>·</span>
              <Link to="/hospice" className="inline-flex items-center gap-1 hover:text-[#A06628]">Hospice directory <ArrowUpRight size={11}/></Link>
              <span>·</span>
              <Link to="/facilities" className="inline-flex items-center gap-1 hover:text-[#1A2622]">All SNFs <ArrowUpRight size={11}/></Link>
              <span>·</span>
              <Link to="/hospitals" className="inline-flex items-center gap-1 hover:text-[#34538F]">All hospitals <ArrowUpRight size={11}/></Link>
            </div>
          </>
        )}
      </main>
      <Footer />
    </div>
  );
}
